import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { categories, posts } from '@/mocks/posts';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import PostCard from '@/components/feature/PostCard';
import { useSeo } from '@/hooks/useSeo';
import { formatViews, getCategoryBySlug, site } from '@/utils/site';

export default function CategoryPage() {
  const { slug = '' } = useParams();
  const category = getCategoryBySlug(slug);
  const [sort, setSort] = useState<'hot' | 'new'>('hot');

  const list = useMemo(() => {
    const base = posts.filter((post) => post.categorySlug === slug);
    return [...base].sort((a, b) =>
      sort === 'hot' ? b.views - a.views : b.date.localeCompare(a.date),
    );
  }, [slug, sort]);

  const hotList = useMemo(() => [...posts].sort((a, b) => b.views - a.views).slice(0, 5), []);

  const allTags = useMemo(() => {
    const set = new Set<string>();
    posts.forEach((post) => post.tags.forEach((tag) => set.add(tag)));
    return Array.from(set).slice(0, 14);
  }, []);

  useSeo({
    title: category
      ? `${category.name}_黑料看点精彩片段合集`
      : `分类未找到_${site.name}`,
    description: category
      ? `${category.name}合集：${category.desc} 黑料看点为你整理该方向的精彩片段与高能名场面，快速抓住事件核心。`
      : '该分类暂不存在，请返回首页浏览其他看点合集。',
    keywords: category
      ? `黑料看点,${category.name},黑料看点精彩片段,黑料看点高能,黑料看点名场面合集`
      : site.keywords,
    path: `/category/${slug}`,
    jsonLd: category
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: category.name,
            description: category.desc,
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
              { '@type': 'ListItem', position: 2, name: category.name },
            ],
          },
        ]
      : undefined,
  });

  if (!category) {
    return (
      <SiteLayout>
        <div className="max-w-3xl mx-auto px-4 md:px-8 pt-32 pb-24 text-center">
          <span className="mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 text-3xl">
            <i className="ri-emotion-sad-line" />
          </span>
          <h1 className="mt-6 font-heading text-3xl text-foreground-950">找不到这个分类</h1>
          <p className="mt-3 text-sm text-foreground-600">你可能访问了一个不存在的分类地址。</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors"
          >
            <i className="ri-arrow-left-line" />
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <section className="relative pt-24 md:pt-32 pb-10 md:pb-14 overflow-hidden bg-foreground-950">
        <img
          src={category.cover}
          alt={`${category.name}分类封面`}
          className="absolute inset-0 w-full h-full object-cover object-top opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950 via-foreground-950/85 to-foreground-950/40" />
        <div className="relative z-10 w-full px-4 md:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="[&_a]:text-background-100/70 [&_span]:text-background-50 [&_i]:text-background-100/40">
              <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: category.name }]} />
            </div>
            <div className="mt-5 flex items-center gap-3">
              <span className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-500 text-background-50 text-xl">
                <i className={category.icon} />
              </span>
              <span className="text-xs tracking-[0.25em] text-accent-300 uppercase">Collection</span>
            </div>
            <h1 className="mt-4 font-heading text-3xl md:text-5xl text-background-50">{category.name}</h1>
            <p className="mt-4 max-w-2xl text-sm md:text-base text-background-100/80 leading-7">{category.desc}</p>
            <p className="mt-4 text-xs text-background-100/60">本分类共 {list.length} 期合集</p>
          </div>
        </div>
      </section>

      <div className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-8">
            <div className="flex items-center justify-between gap-4 mb-6">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950">全部合集</h2>
              <div className="inline-flex items-center p-1 rounded-full bg-background-100 border border-background-200">
                {[
                  { key: 'hot', label: '按热度' },
                  { key: 'new', label: '按最新' },
                ].map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => setSort(option.key as 'hot' | 'new')}
                    className={`px-3.5 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors ${
                      sort === option.key
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-primary-700'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              {list.map((post) => (
                <PostCard key={post.id} post={post} layout="list" />
              ))}
            </div>
          </div>

          <aside className="lg:col-span-4 space-y-6">
            <div className="bg-background-100 rounded-lg border border-background-200 p-5">
              <h2 className="font-heading text-lg text-foreground-950 pb-4 border-b border-background-200">
                热门看点
              </h2>
              <ul className="mt-2">
                {hotList.map((post, index) => (
                  <li key={post.id}>
                    <Link
                      to={`/detail/${post.id}`}
                      className="group flex items-start gap-3 py-3 border-b border-background-200 last:border-0"
                    >
                      <span
                        className={`w-6 h-6 shrink-0 flex items-center justify-center rounded-md font-heading text-xs ${
                          index < 3 ? 'bg-accent-500 text-foreground-950' : 'bg-background-200 text-foreground-700'
                        }`}
                      >
                        {index + 1}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-700 transition-colors">
                          {post.title}
                        </span>
                        <span className="mt-1 block text-[11px] text-foreground-500">
                          {formatViews(post.views)} 次围观
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-background-100 rounded-lg border border-background-200 p-5">
              <h2 className="font-heading text-lg text-foreground-950 pb-4 border-b border-background-200">
                全部分类
              </h2>
              <div className="mt-4 flex flex-wrap gap-2">
                {categories.map((item) => (
                  <Link
                    key={item.slug}
                    to={`/category/${item.slug}`}
                    className={`px-3 py-1.5 rounded-full text-xs transition-colors ${
                      item.slug === slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-50 text-foreground-700 border border-background-200 hover:border-primary-300 hover:text-primary-700'
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>

            <div className="bg-background-100 rounded-lg border border-background-200 p-5">
              <h2 className="font-heading text-lg text-foreground-950 pb-4 border-b border-background-200">
                热门标签
              </h2>
              <div className="mt-4 flex flex-wrap gap-2">
                {allTags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 rounded-full text-xs bg-accent-100 text-accent-900"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}